import {
  DEFAULT_ORG_AVATAR,
  LIBUM_AVATAR_NAMES,
  LIBUM_ORG_AVATAR_NAMES,
} from "@/lib/constants";
import { redirect } from "next/navigation";
import { v4 as uuidv4 } from "uuid";

/**
 * Redirects to a specified path with an encoded message as a query parameter.
 * @param {('error' | 'success')} type - The type of message, either 'error' or 'success'.
 * @param {string} path - The path to redirect to.
 * @param {string} message - The message to be encoded and added as a query parameter.
 * @returns {never} This function doesn't return as it triggers a redirect.
 */
export function encodedRedirect(
  type: "error" | "success",
  path: string,
  message: string,
) {
  return redirect(`${path}?${type}=${encodeURIComponent(message)}`);
}

/**
 * Finds the title of the main navigation item that matches the URL in a data object.
 * @param data The data object to search through
 * @param url The url to search for
 * @returns The title of the main navigation item that matches the url
 */
export function findMainTitleByBaseUrl(
  data: Record<string, any>,
  url: string,
): string {
  for (const navItem of [...data.navMain, ...data.navSecondary]) {
    if (url.includes(navItem.url) && navItem.title != "Upgrade to Plus") {
      return navItem.title;
    }
  }
  return "";
}

/**
 * Finds the groups of a parent page by its base URL in a data object.
 * @param data The data object to search through
 * @param parentUrl The url of the parent page
 * @returns The groups of the parent page
 */
export function findGroupsByBaseUrl(data: any, parentUrl: string): any[] {
  for (const navItem of [...data.navMain, ...data.navSecondary]) {
    if (parentUrl.includes(navItem.url) && navItem.groups) {
      return navItem.groups;
    }
  }
  return [];
}

/**
 * Gets the initials of a name. (ChatGPT)
 * @param name The name to get the initials from
 * @returns The initials of the name
 */
export function getInitials(name: string): string {
  return name
    .split(" ")
    .filter((word) => word.length > 0)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join("");
}

/**
 * Gets the name of the user from the email. (ChatGPT)
 * @param date The date to calculate the days ago
 * @returns The number of days ago
 */
export function daysAgo(date: Date): number {
  const today = new Date();
  const todayUTC = new Date(
    Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()),
  );
  const inputUTC = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
  );

  const diffTime = todayUTC.getTime() - inputUTC.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  return diffDays;
}

/**
 * Gets a random avatar from the list of Libum avatars.
 */
export const getRandomAvatar = (): string => {
  return LIBUM_AVATAR_NAMES[
    Math.floor(Math.random() * LIBUM_AVATAR_NAMES.length)
  ];
};

/**
 * Gets a random avatar from the list of Libum organization avatars.
 */
export const getRandomOrgAvatar = (): string => {
  return `/orgs/${LIBUM_ORG_AVATAR_NAMES[Math.floor(Math.random() * LIBUM_ORG_AVATAR_NAMES.length)]}`;
};

/**
 * Formats a timestamp into a human-readable date.
 * @param timestamp The timestamp to format
 */
export const formatLongDate = (timestamp: number): string => {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  }).format(new Date(timestamp * 1000));
};

/**
 * Gets the logo of the organization.
 * @param logo The logo of the organization
 */
export const getOrganizationLogo = (logo?: string): string => {
  return logo === "UNASSIGNED"
    ? DEFAULT_ORG_AVATAR
    : logo || DEFAULT_ORG_AVATAR;
};

/**
 * Gets the name of the user from the email. (ChatGPT)
 * @param email The email to get the name from
 */
export const getNameFromEmail = (email: string): string => {
  const match = email.match(/^([^@]+)/);
  if (!match) return "";

  // Split the username part by dots, underscores, or hyphens
  const parts = match[1].split(/[\.\-_]/);

  // Capitalize each part and join them as a name
  return parts
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

/**
 * Gets the name of the user.
 * @param name The name of the user
 * @param fallbackEmail The email to fallback with if the name isn't set
 */
export const getUserName = (
  name: string,
  fallbackEmail: string = "",
): string => {
  return name === "UNASSIGNED" ? getNameFromEmail(fallbackEmail) : name;
};

/**
 * Parses a string of emails separated by commas.
 * @param input The input string to parse
 */
export const parseEmails = (input: string): string[] =>
  input
    .split(",")
    .map((email) => email.trim())
    .filter(Boolean);

/**
 * Generates an invite token
 */
export const generateInviteToken = (): string => {
  return `inv_${uuidv4()}`;
};

/**
 * Generates a unique user ID.
 */
export const generateUserId = (): string => {
  return `user_${uuidv4()}`;
};
